import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { proposalHref } from '../../../lib/proposalHref';
import { useLandscapeStory } from '../hooks/useLandscapes';
const kinds = {
    opportunity: 'Opportunity',
    capture_project: 'Pursuit',
    proposal: 'Proposal',
    rfi_response: 'RFI Response',
    notice: 'Notice',
    award: 'Award',
    program: 'Program reference'
};
const categories = {
    market_published: 'Market published',
    company_action: 'Company action',
    learned_associated: 'Learned / associated'
};
const actions = {
    created: 'Created',
    archived: 'Archived',
    manual_add: 'Associated manually',
    manual_remove: 'Relationship removed',
    rule_add: 'Associated by rule',
    rule_suppressed: 'Rule suggestion suppressed',
    notice_linked: 'Notice associated',
    published: 'Published',
    awarded: 'Awarded'
};
// Only routes with a record detail surface. Program references do not grant Delivery access.
function sourcePath(record) {
    if (!record.current || !record.id) return undefined;
    const id = encodeURIComponent(record.id);
    switch(record.kind){
        case 'capture_project':
            return `/capture/${id}`;
        case 'proposal':
            return proposalHref(record.id);
        case 'rfi_response':
            return `/rfi-response/${id}`;
        case 'notice':
            return `/opportunities?notice=${id}`;
        default:
            return undefined;
    }
}
function dateText(event) {
    if (!event.occurredAt) return 'Date not recorded';
    const date = event.occurredAt.toDate();
    if (!Number.isFinite(date.getTime())) return 'Date not recorded';
    return date.toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC'
    }) + ' UTC';
}
function StoryEvent({ event }) {
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.landscapes.storyEvent,
        "data-category": event.category,
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "outline",
                        children: categories[event.category] ?? 'Recorded activity'
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: actions[event.action] ?? event.action.replace(/_/g, ' ')
                    }),
                    /*#__PURE__*/ _jsx("time", {
                        dateTime: event.occurredAt?.toDate().toISOString(),
                        children: dateText(event)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("details", {
                className: "text-sm text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        children: "Date provenance"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Source: ",
                            event.sourceTable || 'Not supplied',
                            " · ",
                            event.sourceId || 'Not supplied',
                            " · Date field: ",
                            event.dateField || 'Not supplied'
                        ]
                    })
                ]
            })
        ]
    });
}
export function LandscapeStory({ landscapeId, includeRemoved }) {
    const story = useLandscapeStory(landscapeId, includeRemoved);
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const timeline = params.get('story') === 'timeline';
    const selected = params.get('selected');
    const change = (key, value)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            next.set(key, value);
            return next;
        }, {
            replace: true,
            state: location.state
        });
    const data = story.data;
    const recordHeading = (record)=>{
        const path = sourcePath(record);
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    children: kinds[record.kind] ?? 'Reference'
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "font-medium",
                    children: record.title || 'Untitled record'
                }),
                /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "outline",
                    children: record.status || 'Status not recorded'
                }),
                !record.current && /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "Earlier record"
                }),
                path && /*#__PURE__*/ _jsxs(Link, {
                    to: path,
                    state: {
                        returnTo: location.pathname + location.search
                    },
                    "data-testid": Testids.landscapes.storySource,
                    onClick: ()=>change('selected', `${record.kind}:${record.id}`),
                    className: "text-primary underline",
                    children: [
                        "Open ",
                        kinds[record.kind] ?? 'source'
                    ]
                })
            ]
        });
    };
    const renderRecord = (record)=>/*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.landscapes.storyRecord,
            "data-kind": record.kind,
            "data-record-id": record.id,
            "data-current-work": record.currentWork,
            "aria-current": selected === `${record.kind}:${record.id}` ? 'true' : undefined,
            className: "space-y-2 border-t border-border py-3",
            children: [
                recordHeading(record),
                record.entities.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm text-muted-foreground",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "Shared entity context"
                        }),
                        record.entities.map((entity, index)=>/*#__PURE__*/ _jsxs("p", {
                                children: [
                                    entity.kind.replace(/_/g, ' '),
                                    ": ",
                                    entity.name || 'Name not supplied',
                                    entity.identifier && ` · ${entity.identifier}`,
                                    " · Source:",
                                    ' ',
                                    entity.sourceId || 'Not supplied'
                                ]
                            }, `${entity.kind}:${entity.sourceId}:${index}`))
                    ]
                }),
                record.history.length === 0 ? /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "No dated history supplied."
                }) : record.history.map((event)=>/*#__PURE__*/ _jsx(StoryEvent, {
                        event: event
                    }, event.id))
            ]
        }, `${record.kind}:${record.id}`);
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.landscapes.storyPanel,
        "aria-labelledby": "landscape-story-title",
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                id: "landscape-story-title",
                className: "text-base font-semibold",
                children: "Market & Work"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Each Opportunity is one business thread. Pursuits, RFI Responses and Proposals are separate work histories, not additional deals. Only permitted sources are shown; missing evidence is not proof of no activity."
            }),
            /*#__PURE__*/ _jsxs("fieldset", {
                className: "flex flex-wrap gap-2",
                "aria-label": "Story view",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "outline",
                        "aria-pressed": !timeline,
                        "data-testid": Testids.landscapes.storyGrouped,
                        onClick: ()=>change('story', 'grouped'),
                        children: "Grouped / current work"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "outline",
                        "aria-pressed": timeline,
                        "data-testid": Testids.landscapes.storyTimeline,
                        onClick: ()=>change('story', 'timeline'),
                        children: "Dated timeline"
                    })
                ]
            }),
            story.isLoading && /*#__PURE__*/ _jsxs("output", {
                "data-testid": Testids.landscapes.storyLoading,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Loading Market & Work…"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-12 w-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-24 w-full"
                    })
                ]
            }),
            story.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Could not load Market & Work."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: story.isFetching,
                        onClick: ()=>void story.refetch(),
                        "data-testid": Testids.landscapes.storyRetry,
                        children: "Retry story"
                    })
                ]
            }),
            !story.isLoading && !story.isError && data && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        "data-testid": Testids.landscapes.storyCounts,
                        className: "text-sm",
                        children: [
                            data.opportunityCount,
                            " Opportunities · ",
                            data.pursuitCount,
                            " Pursuits ·",
                            ' ',
                            data.rfiResponseCount,
                            " RFI Responses · ",
                            data.proposalCount,
                            " Proposals ·",
                            ' ',
                            data.programCount,
                            " Program references"
                        ]
                    }),
                    data.threads.length === 0 && /*#__PURE__*/ _jsx("p", {
                        "data-testid": Testids.landscapes.storyEmpty,
                        className: "text-sm text-muted-foreground",
                        children: "No permitted activity to show. Add an Opportunity in Members, or show removed relationships to review earlier associations."
                    }),
                    data.threads.map((thread)=>{
                        const work = thread.records.filter((r)=>[
                                'capture_project',
                                'proposal',
                                'rfi_response'
                            ].includes(r.kind));
                        return /*#__PURE__*/ _jsx(SpecraCard, {
                            "data-testid": Testids.landscapes.storyThread,
                            "data-opportunity-id": thread.opportunityId,
                            children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                className: "space-y-3 pt-4",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "font-semibold",
                                        children: thread.records.find((r)=>r.kind === 'opportunity')?.title || 'Untitled Opportunity'
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: [
                                            "One business thread ·",
                                            ' ',
                                            thread.membershipState === 'removed' ? 'Removed relationship — underlying work unchanged' : 'Associated with this Landscape'
                                        ]
                                    }),
                                    selected && thread.records.some((r)=>`${r.kind}:${r.id}` === selected) && /*#__PURE__*/ _jsxs("p", {
                                        "data-testid": Testids.landscapes.storySelected,
                                        className: "text-sm",
                                        children: [
                                            "Returning to selected source:",
                                            ' ',
                                            thread.records.find((r)=>`${r.kind}:${r.id}` === selected)?.title || 'Untitled record'
                                        ]
                                    }),
                                    !timeline && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("h4", {
                                                className: "font-medium",
                                                children: "Opportunity & market references"
                                            }),
                                            thread.records.filter((r)=>!work.includes(r)).map(renderRecord),
                                            /*#__PURE__*/ _jsx("h4", {
                                                className: "font-medium",
                                                children: "Current work"
                                            }),
                                            work.filter((r)=>r.currentWork).map(renderRecord),
                                            !work.some((r)=>r.currentWork) && /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No current work recorded in permitted sources."
                                            }),
                                            /*#__PURE__*/ _jsx("h4", {
                                                className: "font-medium",
                                                children: "Earlier, stopped & other recorded work"
                                            }),
                                            work.filter((r)=>!r.currentWork).map(renderRecord),
                                            !work.some((r)=>!r.currentWork) && /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No earlier work supplied."
                                            })
                                        ]
                                    })
                                ]
                            })
                        }, thread.opportunityId);
                    }),
                    timeline && data.threads.length > 0 && /*#__PURE__*/ _jsxs("ol", {
                        "aria-label": "Dated activity across business threads",
                        className: "space-y-4",
                        children: [
                            data.threads.flatMap((thread)=>thread.records.flatMap((record)=>record.history.map((event)=>({
                                            thread,
                                            record,
                                            event
                                        })))).sort((a, b)=>(a.event.occurredAt?.toDate().getTime() ?? Number.POSITIVE_INFINITY) - (b.event.occurredAt?.toDate().getTime() ?? Number.POSITIVE_INFINITY) || a.event.id.localeCompare(b.event.id)).map(({ thread, record, event })=>/*#__PURE__*/ _jsxs("li", {
                                    className: "space-y-2 border-t border-border py-3",
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: [
                                                "Opportunity:",
                                                ' ',
                                                thread.records.find((r)=>r.kind === 'opportunity')?.title || 'Untitled Opportunity'
                                            ]
                                        }),
                                        recordHeading(record),
                                        /*#__PURE__*/ _jsx(StoryEvent, {
                                            event: event
                                        })
                                    ]
                                }, `${thread.opportunityId}:${record.kind}:${record.id}:${event.id}`)),
                            !data.threads.some((thread)=>thread.records.some((record)=>record.history.length > 0)) && /*#__PURE__*/ _jsx("li", {
                                children: "No dated history supplied."
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
